/**
 * SetPasswordPage — « Définir mon mot de passe » (L1bis.7, Q16-c).
 *
 * Écran PUBLIC ouvert par le lien d’invitation « #set_password_token=… » (email
 * envoyé par creer-technicien-sav v1.8 ou par « Renvoyer l'invitation »). Deux
 * champs, POST /api/auth/set-password, erreurs par code en français, succès →
 * « Se connecter ». Le jeton ne va jamais dans localStorage ni dans console.log.
 */

import React, { useState } from 'react';
import { useViewport } from '../hooks/useViewport';

interface Props {
  token: string;
  onDone: () => void;
}

const API_BASE = window.location.origin;

const MESSAGES: Record<string, string> = {
  INVALID_TOKEN: 'Ce lien d’invitation n’est pas valide. Demandez à un administrateur de renvoyer l’invitation.',
  TOKEN_USED: 'Ce lien a déjà été utilisé. Demandez à un administrateur de renvoyer l’invitation.',
  TOKEN_EXPIRED: 'Ce lien a expiré (il était valable 7 jours). Demandez à un administrateur de renvoyer l’invitation.',
  PASSWORD_TOO_SHORT: 'Le mot de passe doit contenir au moins 6 caractères.',
  MISSING_PARAMETERS: 'Saisissez un mot de passe.',
  UPDATE_FAILED: 'Le mot de passe n’a pas pu être enregistré. Demandez à un administrateur de renvoyer l’invitation.',
  PASSWORD_TOO_LONG: 'Le mot de passe est trop long (72 octets au plus, environ 72 caractères sans accents).',
  PASSWORD_REFUSE: 'Ce mot de passe est refusé (trop faible ou trop long). Choisissez-en un autre : le lien reste valable.',
  COMPTE_DESACTIVE: 'Ce compte est désactivé. Adressez-vous à un administrateur.',
};

// GoTrue refuse au-delà de 72 octets : contrôlé ici aussi, pour ne pas faire un aller-retour inutile.
const OCTETS_MAX = 72;

function SetPasswordPage({ token, onDone }: Props) {
  const { isPhone } = useViewport();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 6) { setError(MESSAGES.PASSWORD_TOO_SHORT); return; }
    if (new TextEncoder().encode(password).length > OCTETS_MAX) { setError(MESSAGES.PASSWORD_TOO_LONG); return; }
    if (password !== confirmation) { setError('Les deux mots de passe ne sont pas identiques.'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        const code: string = result?.error?.code || '';
        console.error('[SetPassword] refus :', code || response.status);
        setError(MESSAGES[code] || result?.error?.message || 'Erreur de connexion au serveur.');
        return;
      }
      setSuccess(true);
    } catch (err) {
      console.error('[SetPassword] Erreur réseau');
      setError(err instanceof Error && err.message ? 'Erreur de connexion au serveur.' : 'Erreur de connexion au serveur.');
    } finally {
      setLoading(false);
    }
  }

  const champ = (label: string, valeur: string, setter: (v: string) => void, autoFocus = false) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>{label}</label>
      <input
        type="password"
        value={valeur}
        onChange={(e) => setter(e.target.value)}
        required
        minLength={6}
        autoFocus={autoFocus}
        autoComplete="new-password"
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
        onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; }}
        onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; }}
      />
    </div>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f1f5f9', padding: '20px' }}>
      <div style={{ maxWidth: isPhone ? 'calc(100vw - 24px)' : '460px', width: '100%', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', padding: isPhone ? '24px' : '40px' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ width: '64px', height: '64px', backgroundColor: success ? '#dcfce7' : '#dbeafe', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            {success ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
            )}
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>
            {success ? 'Mot de passe défini' : 'Définir mon mot de passe'}
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
            {success
              ? 'Vous pouvez maintenant vous connecter avec votre adresse email et ce mot de passe.'
              : 'Choisissez le mot de passe de votre compte ServUp (6 caractères au moins). Ce lien est à usage unique.'}
          </p>
        </div>

        {success ? (
          <button type="button" onClick={onDone}
            style={{ width: '100%', padding: '12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
            Se connecter
          </button>
        ) : (
          <form onSubmit={handleSubmit}>
            {champ('Nouveau mot de passe', password, setPassword, true)}
            {champ('Confirmez le mot de passe', confirmation, setConfirmation)}
            {error && (
              <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: '#991b1b' }}>
                {error}
              </div>
            )}
            <button type="submit" disabled={loading}
              style={{ width: '100%', padding: '12px', backgroundColor: loading ? '#93c5fd' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', marginBottom: '12px' }}>
              {loading ? 'Enregistrement…' : 'Enregistrer mon mot de passe'}
            </button>
            <button type="button" onClick={onDone}
              style={{ width: '100%', padding: '10px', backgroundColor: 'transparent', color: '#64748b', border: 'none', fontSize: '14px', cursor: 'pointer' }}>
              ← Retour à la connexion
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default SetPasswordPage;
